/* teko-cartdrawer-fix.js
   Fix for themes where the cart drawer UI doesn't refresh totals/promos after quantity changes.
   - Listens for qty +/- / input change / remove inside the cart drawer.
   - Calls Shopify AJAX cart change endpoint and re-renders the cart-drawer section + cart icon bubble.
   - Does NOT require redefining Dawn's custom elements (avoids conflicts).
*/
(() => {
  const DRAWER_ROOT_ID = 'CartDrawer';
  const DRAWER_INNER_SELECTOR = '.drawer__inner';
  const SECTION_CART_DRAWER = 'cart-drawer';
  const SECTION_BUBBLE = 'cart-icon-bubble';

  const $ = (sel, root=document) => root.querySelector(sel);

  const inDrawer = (el) => !!(el && el.closest && el.closest(`#${DRAWER_ROOT_ID}`));

  const getLineFromContext = (el) => {
    // Dawn-compatible data-index on buttons or inputs
    const byDataIndex = el.closest('[data-index]')?.dataset?.index;
    if (byDataIndex) return parseInt(byDataIndex, 10);

    // Quantity input
    const input = el.closest('quantity-input')?.querySelector('input.quantity__input') ||
                  el.closest('.quantity')?.querySelector('input.quantity__input');
    const idx = input?.dataset?.index;
    return idx ? parseInt(idx, 10) : NaN;
  };

  const getQtyInput = (el) =>
    el.closest('quantity-input')?.querySelector('input.quantity__input') ||
    el.closest('.quantity')?.querySelector('input.quantity__input') ||
    null;

  const getSectionsToRender = () => [SECTION_CART_DRAWER, SECTION_BUBBLE];

  const renderSectionInnerHTML = (html, selector) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.querySelector(selector);
  };

  const replaceDrawerInner = (drawerHtml) => {
    const drawer = document.getElementById(DRAWER_ROOT_ID);
    if (!drawer) return;

    const newInner = renderSectionInnerHTML(drawerHtml, DRAWER_INNER_SELECTOR);
    const oldInner = drawer.querySelector(DRAWER_INNER_SELECTOR);

    if (newInner && oldInner) oldInner.replaceWith(newInner);
  };

  const replaceBubble = (bubbleHtml) => {
    // IMPORTANT:
    // On this theme, #cart-icon-bubble lives inside the HEADER section.
    // Replacing the closest .shopify-section would wipe the entire header (menu).
    // So we ONLY replace the contents of the bubble element.
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    const tmp = document.createElement('div');
    tmp.innerHTML = bubbleHtml;

    const newBubble = tmp.querySelector('#cart-icon-bubble');
    if (!newBubble) return;

    bubble.innerHTML = newBubble.innerHTML;
  };

  const changeLineQty = async (line, quantity) => {
    if (!Number.isFinite(line)) return;

    const body = {
      line,
      quantity,
      sections: getSectionsToRender(),
      sections_url: window.location.pathname
    };

    const res = await fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!res.ok) throw new Error(`Cart change failed: ${res.status}`);
    const state = await res.json();

    // Shopify returns: { sections: { 'cart-drawer': '<section...>', 'cart-icon-bubble': '<section...>' }, ... }
    const sections = state.sections || {};
    if (sections[SECTION_CART_DRAWER]) replaceDrawerInner(sections[SECTION_CART_DRAWER]);
    if (sections[SECTION_BUBBLE]) replaceBubble(sections[SECTION_BUBBLE]);
  };

  const onClick = async (e) => {
    if (!inDrawer(e.target)) return;

    // Remove button inside <cart-remove-button>
    const remove = e.target.closest('cart-remove-button a, cart-remove-button button, [data-cart-remove]');
    if (remove) {
      e.preventDefault();
      const line = getLineFromContext(remove);
      try { await changeLineQty(line, 0); } catch (err) { console.error(err); window.location.reload(); }
      return;
    }

    // +/- buttons
    const minus = e.target.closest('button[name="minus"]');
    const plus  = e.target.closest('button[name="plus"]');
    if (!minus && !plus) return;

    e.preventDefault();

    const btn = minus || plus;
    const input = getQtyInput(btn);
    if (!input) return;

    const line = parseInt(input.dataset.index || '', 10) || getLineFromContext(btn);
    const current = parseInt(input.value || '0', 10);
    const step = Math.max(1, parseInt(input.getAttribute('step') || '1', 10));
    const min = Math.max(0, parseInt(input.getAttribute('min') || '0', 10));
    const maxAttr = input.getAttribute('max');
    const max = maxAttr == null ? Infinity : Math.max(0, parseInt(maxAttr, 10));

    let next = current + (plus ? step : -step);
    next = Math.max(min, Math.min(max, next));

    try { await changeLineQty(line, next); } catch (err) { console.error(err); window.location.reload(); }
  };

  const onChange = async (e) => {
    if (!inDrawer(e.target)) return;

    const input = e.target.closest('input.quantity__input');
    if (!input) return;

    const line = parseInt(input.dataset.index || '', 10);
    const qty = Math.max(0, parseInt(input.value || '0', 10));

    if (!Number.isFinite(line)) return;

    try { await changeLineQty(line, qty); } catch (err) { console.error(err); window.location.reload(); }
  };

  // Use capture to beat other handlers that may be buggy
  document.addEventListener('click', (e) => { onClick(e); }, true);
  document.addEventListener('change', (e) => { onChange(e); }, true);
})();
